(ns ui.wallet.rates
  "The rates table of the wallet page, shown in a modal: what each service
   costs, with the customer's discounts. The service names and descriptions come from the
   server (`app.subscription.services`) but are shown from the dictionaries,
   keyed by service id; a service the dictionaries do not know keeps the
   server's text (see `ui.wallet.i18n/on-missing-key`)."
  (:require [parts.replicant.query :as query]
            [ui.elements]
            [ui.subscription.core :as core]
            [ui.wallet.i18n :as i18n]))

(def days-per-month
  31)

(defn format-price
  [price]
  (str "$" (.toFixed (or price 0) 2)))

(defn discount-badge
  [discount]
  (when discount
    [:span.inline-flex.items-center.rounded-md.bg-green-50.px-2.py-1.text-xs.font-medium.text-green-700.ring-1.ring-inset.ring-green-600
     [:i18n :rates/off {:percent (int (* 100 discount))}]]))

(defn price
  "The price, struck through above the discounted one when a discount
   applies."
  [regular discounted]
  (if (= regular discounted)
    (format-price regular)
    [:div
     [:span.text-sm.text-gray-500.line-through (format-price regular)]
     [:br]
     [:span.text-green-600.font-semibold (format-price discounted)]]))

(defn unit-label
  [billing-unit]
  (case billing-unit
    :day [:i18n :rates/per-day]
    :post [:i18n :rates/per-post]
    :image [:i18n :rates/per-image]
    nil))

(defn service-row
  [discounts {:keys [id name description billing-unit] :as service}]
  (let [discount (get discounts id)
        regular (:price service)
        discounted (if discount
                     (* regular (- 1.0 discount))
                     regular)]
    [:tr
     ;; The description has to wrap: with `whitespace-nowrap` the longer ones
     ;; made the table wider than the modal and pushed the price columns out
     ;; of its box (the modal panel does not clip its overflow).
     [:td {:class "py-2 pl-4 pr-3 text-sm sm:pl-0"}
      [:div.flex.items-center.gap-2
       [:span [:i18n (i18n/service-key id :name) {:fallback name}]]
       (discount-badge discount)]
      [:div {:class "text-gray-500"}
       [:i18n (i18n/service-key id :description) {:fallback description}]]]
     [:td {:class "whitespace-nowrap px-2 py-2 text-sm"}
      (price regular discounted)
      [:div {:class "text-gray-500"}
       (unit-label billing-unit)]]
     [:td {:class "whitespace-nowrap px-2 py-2 text-sm"}
      (when (= billing-unit :day)
        (list
          (price (* regular days-per-month)
                 (* discounted days-per-month))
          [:div {:class "text-gray-500"}
           [:i18n :rates/per-days {:days days-per-month}]]))]]))

(defn table
  [w]
  (let [{:keys [services discounts]} (query/get-result w core/subscription-query)]
    [:div
     [:h3 {:class ui.elements/h3} [:i18n :rates/title]]
     [:p {:class (concat ui.elements/p-sm ["mt-2" "mb-4"])}
      [:i18n :rates/intro]
      [:span.font-bold [:i18n :rates/intro-bold]]]
     [:table {:class ["min-w-full" "divide-y" "divide-gray-300"]}
      [:tbody {:class "divide-y divide-gray-200"}
       (map (fn [service]
              (service-row discounts service))
            services)]]
     [:p.text-xs.text-gray-500.mt-4 [:i18n :rates/excl-vat]]]))

(defn render
  "Modals render outside `ui.wallet.page/render`, so the rates
   modal has to translate itself."
  [w]
  (i18n/translate w (table w)))

(def modal
  {:modal-id :modals/subscription-rates-modal
   :render #'render})

(defn link
  "Opens the rates modal. The table used to sit at the bottom of the wallet
   page, where it confused more people than it helped."
  []
  [:button
   {:type "button"
    :class ["text-sm" "text-gray-500" "underline" "hover:text-gray-700"]
    :on {:click [[:ui/modal-open :modals/subscription-rates-modal
                  {:max-width "sm:max-w-2xl"}]]}}
   [:i18n :rates/view]])
